import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/invoice/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/invoice/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/invoice/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/invoice/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/invoice/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/invoice/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/invoice/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/invoice/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/invoice/UPDATE_FAILURE'
const SHARE_REQUEST = 'realhub/invoice/SHARE_REQUEST'
const SHARE_SUCCESS = 'realhub/invoice/SHARE_SUCCESS'
const SHARE_FAILURE = 'realhub/invoice/SHARE_FAILURE'

// Initial State
const initialState = {
  loaded: false,
  loadedIds: [],
  loading: false,
  creating: false,
  sharing: false,
  updating: false,
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function shareRequest(){
  return {
    type: SHARE_REQUEST,
  }
}

export function shareSuccess(){
  return {
    type: SHARE_SUCCESS,
  }
}

export function shareFailure(errors = []){
  return {
    type: SHARE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadInvoices(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/admin/invoices.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadInvoice(invoice, options){
  return (dispatch, getState) => {
    const state = getState()
    const loadedIds = state.invoices.loadedIds ? state.invoices.loadedIds.slice() : []
    if (!loadedIds.includes(invoice.id)){
      loadedIds.push(invoice.id)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/admin/invoices/${invoice.id}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createInvoice(invoice, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      invoice,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api('/admin/invoices.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function approveInvoice(invoice, options, successCallback, errorCallback){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/invoices/${invoice.id}/approve.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (successCallback) successCallback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        if (errorCallback) errorCallback(json)

        return { success: false, result: errors }
      })

    return promise
  }
}

export function voidInvoice(invoice, options, successCallback, errorCallback){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/invoices/${invoice.id}/void.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (successCallback) successCallback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        if (errorCallback) errorCallback(json)

        return { success: false, result: errors }
      })

    return promise
  }
}

export function sendInvoice(invoice, options, callback){
  const config = {
    method: 'POST',
  }

  return () => {
    const promise = api(`/admin/invoices/${invoice.id}/send_invoice.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)

        return { success: false, result: errors }
      })

    return promise
  }
}

export function updateInvoice(invoice, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      invoice,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/invoices/${invoice.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function searchInvoices(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/admin/invoices/search.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function syncPaidInvoices(options, callback){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api('/admin/invoices/synchronise_paid_invoices.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function syncExternalInvoice(invoice, options, successCallback, errorCallback){
  const config = {
    method: 'PUT',
  }

  return () => {
    const promise = api(`/admin/invoices/${invoice.id}/sync_external_invoice.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        if (successCallback) successCallback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        if (errorCallback) errorCallback(json)

        return { success: false, result: errors }
      })

    return promise
  }
}

export function createExternalInvoice(invoice, options, successCallback, errorCallback){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api(`/admin/invoices/${invoice.id}/create_external_invoice.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (successCallback) successCallback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        if (errorCallback) errorCallback(json)
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function mergeInvoices(invoiceTemplate, invoiceIds, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      invoice: invoiceTemplate,
      invoice_ids: invoiceIds,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api('/admin/invoices/merge_invoices.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.INVOICE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return json
      })
      .then(() => {
        const { invoices } = getState().entities
        const updatedInvoices = invoiceIds.map((id) => {
          const updatedInvoice = invoices[id] || {}
          updatedInvoice.void = true

          return updatedInvoice
        })

        const normalizedJson = normalize(updatedInvoices, Schemas.INVOICE_ARRAY)
        dispatch(updateEntities(normalizedJson))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function sendSms(invoice, mobileNumbers = [], message = ''){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      v2: true,
      mobile_numbers: mobileNumbers,
      message,
    }),
  }

  return (dispatch) => {
    dispatch(shareRequest())

    const promise = api(`/admin/invoices/${invoice.id}/send_sms.json`, {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        dispatch(shareSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(shareFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedIds: action.loadedIds || state.loadedIds,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case SHARE_REQUEST:
      return {
        ...state,
        sharing: true,
      }
    case SHARE_SUCCESS:
      return {
        ...state,
        sharing: false,
        errors: [],
      }
    case SHARE_FAILURE:
      return {
        ...state,
        sharing: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
